import { useEffect, useState } from "react";
import { getConsent } from "./CookieBanner";
import { initSentryFrontend } from "../lib/sentryInit";
import { api } from "../lib/api";

/**
 * Loads analytics & marketing scripts only AFTER the user has granted consent
 * via the cookie banner. IDs are read at runtime from /api/settings first
 * (admin-editable, no rebuild required), with fallback to env vars.
 * Each script is gated by its ID being non-empty AND the matching consent
 * category. No ID = no script loaded = zero requests, zero warnings.
 *
 * Configure IDs from Admin → Réglages → Analytics & Marketing:
 *   ga4_id, gtm_id, meta_pixel_id, tiktok_pixel_id, clarity_project_id
 *
 * Fallback env vars (frontend/.env, kept for backward compat):
 *   REACT_APP_GA4_ID, REACT_APP_GTM_ID, REACT_APP_META_PIXEL_ID,
 *   REACT_APP_TIKTOK_PIXEL_ID, REACT_APP_CLARITY_PROJECT_ID
 */
export default function AnalyticsScripts() {
  const [consent, setConsentState] = useState(() => getConsent());
  const [ids, setIds] = useState({
    ga4: process.env.REACT_APP_GA4_ID || "",
    gtm: process.env.REACT_APP_GTM_ID || "",
    metaPixel: process.env.REACT_APP_META_PIXEL_ID || "",
    tiktokPixel: process.env.REACT_APP_TIKTOK_PIXEL_ID || "",
    clarity: process.env.REACT_APP_CLARITY_PROJECT_ID || "",
  });

  // Re-evaluate when user updates consent preferences
  useEffect(() => {
    const handler = (e) => setConsentState(e.detail || getConsent());
    window.addEventListener("cookie-consent-changed", handler);
    return () => window.removeEventListener("cookie-consent-changed", handler);
  }, []);

  // Fetch dynamic IDs from /api/settings (admin-editable, overrides env)
  useEffect(() => {
    api.get("/settings").then((r) => {
      const s = r.data || {};
      setIds((prev) => ({
        ga4: s.ga4_id || prev.ga4,
        gtm: s.gtm_id || prev.gtm,
        metaPixel: s.meta_pixel_id || prev.metaPixel,
        tiktokPixel: s.tiktok_pixel_id || prev.tiktokPixel,
        clarity: s.clarity_project_id || prev.clarity,
      }));
    }).catch(() => { /* keep env fallback */ });
    // Reload when admin updates settings
    const handler = () => {
      api.get("/settings").then((r) => {
        const s = r.data || {};
        setIds({
          ga4: s.ga4_id || "",
          gtm: s.gtm_id || "",
          metaPixel: s.meta_pixel_id || "",
          tiktokPixel: s.tiktok_pixel_id || "",
          clarity: s.clarity_project_id || "",
        });
      }).catch(() => {});
    };
    window.addEventListener("site-settings-updated", handler);
    return () => window.removeEventListener("site-settings-updated", handler);
  }, []);

  // Sentry is loaded unconditionally (no personal data; only errors)
  useEffect(() => { initSentryFrontend(); }, []);

  useEffect(() => {
    if (!consent) return;
    const { ga4, gtm, clarity, metaPixel, tiktokPixel } = ids;

    // === ANALYTICS ===
    if (consent.analytics) {
      if (ga4 && !window.gtag) {
        const s = document.createElement("script");
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${ga4}`;
        document.head.appendChild(s);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () { window.dataLayer.push(arguments); };
        window.gtag("js", new Date());
        window.gtag("config", ga4, { anonymize_ip: true });
      }
      if (gtm && !window.__gtm_loaded) {
        window.__gtm_loaded = true;
        const s = document.createElement("script");
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtm.js?id=${gtm}`;
        document.head.appendChild(s);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      }
      if (clarity && !window.clarity) {
        (function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script",clarity);
      }
    }

    // === MARKETING ===
    if (consent.marketing) {
      if (metaPixel && !window.fbq) {
        !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
        window.fbq("init", metaPixel);
        window.fbq("track", "PageView");
      }
      if (tiktokPixel && !window.ttq) {
        !function (w, d, t) {w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var p=d.createElement("script");p.type="text/javascript",p.async=!0,p.src=r+"?sdkid="+e+"&lib="+t;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(p,s)};ttq.load(tiktokPixel);ttq.page();}(window, document, 'ttq');
      }
    }
  }, [consent, ids]);

  return null;
}

/**
 * Helper to track e-commerce events across GA4 / Meta / TikTok in one shot.
 * Usage: trackEvent('purchase', { value: 49.90, currency: 'EUR', items: [...] })
 */
export function trackEvent(name, params = {}) {
  try {
    if (window.gtag) window.gtag("event", name, params);
    if (window.fbq) {
      const fbMap = { purchase: "Purchase", add_to_cart: "AddToCart", begin_checkout: "InitiateCheckout", view_item: "ViewContent", sign_up: "CompleteRegistration" };
      window.fbq("track", fbMap[name] || "CustomEvent", params);
    }
    if (window.ttq) {
      const ttMap = { purchase: "CompletePayment", add_to_cart: "AddToCart", begin_checkout: "InitiateCheckout", view_item: "ViewContent", sign_up: "CompleteRegistration" };
      window.ttq.track(ttMap[name] || "ClickButton", params);
    }
  } catch { /* silent */ }
}

